import classNames from 'classnames';
import React, { FC, useEffect, useMemo, useRef, useState, useCallback, CSSProperties } from 'react';
import { GetSize } from 'utils/StringUtils';
import {
  ChunkUploader,
  IFileUploadCompleteInfo,
  IFileUploadProgressInfo,
} from 'components/FileLoader/Helpers/chunkuploader';
import { uploadFile } from '../Helpers/fileuploader';
import { ProgressBar } from './ProgressBar';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import { Button } from 'components/buttons/Button/Button';
import { downloadFile } from 'utils/FileUtils';

export interface IUploadedFileInfo {
  fileId: string;
  fileName: string;
  fileSize: number;
}

export interface IUploadCompleteInfo extends IFileUploadCompleteInfo {
  tempFileId: string;
}

export interface IFileInfo extends IUploadedFileInfo {
  uploaded: boolean;
  file?: File;
}

export interface IFileInfoProps {
  fileInfo: IFileInfo;
  onComplete?: (info: IUploadCompleteInfo) => void;
  onRemove?: (info: IUploadedFileInfo) => void;
  isDisabled?: boolean;
  disabledDownload?: boolean;
  fileNameClassName?: string;
  fontCssStyles?: CSSProperties;
}

export const FileInfo: FC<IFileInfoProps> = props => {
  const chunkUploader = useRef<ChunkUploader | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const fileSize = GetSize(props.fileInfo.fileSize);
  let transitionTimeout = useRef<number>();

  useEffect(() => {
    if (props.fileInfo.uploaded && chunkUploader.current) chunkUploader.current = undefined;
  }, [props.fileInfo.uploaded]);

  useEffect(() => {
    return () => {
      if (chunkUploader.current) chunkUploader.current.cancel(); //I am going away
    };
  }, []);

  const handleProgress = (info: IFileUploadProgressInfo) => {
    setProgress(info.loaded);
  };

  const handleUploadComplete = useCallback(
    (info: IFileUploadCompleteInfo) => {
      //let the progress bar finish its trasition
      clearTimeout(transitionTimeout.current);
      transitionTimeout.current = window.setTimeout(() => {
        chunkUploader.current = undefined;
        props.onComplete && props.onComplete({ ...info, tempFileId: props.fileInfo.fileId });
      }, 300);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onComplete, props.fileInfo]
  );

  const handleRemove = useCallback(() => {
    clearTimeout(transitionTimeout.current);
    if (props.isDisabled) return;
    if (!props.fileInfo.uploaded && chunkUploader.current) chunkUploader.current.cancel();
    props.onRemove && props.onRemove({ ...props.fileInfo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onRemove, props.fileInfo]);

  const handleDownload = useCallback(() => {
    if (props.isDisabled && props.disabledDownload) return;
    const encodedFileName = encodeURIComponent(props.fileInfo.fileName);
    const url = `${Decisions.DataTypes.baseURL}/FileReferenceService/JS/DownloadFile?id=${props.fileInfo.fileId}&fileName=${encodedFileName}`;
    if (url) downloadFile(url);
  }, [props.isDisabled, props.fileInfo, props.disabledDownload]);

  useEffect(() => {
    if (!props.fileInfo.uploaded && props.fileInfo.file && !chunkUploader.current) {
      chunkUploader.current = uploadFile({
        file: props.fileInfo.file,
        callbacks: {
          onProgress: handleProgress,
          onComplete: handleUploadComplete,
        },
      });
    }
  }, [props.fileInfo, handleUploadComplete]);

  const progressBar = useMemo(() => {
    if (props.fileInfo.uploaded || !props.fileInfo.file) return null;
    return (
      <ProgressBar totalSize={props.fileInfo.file.size} finished={progress}>
        <span className="dp-file-upload-area__file-size">{fileSize}</span>
      </ProgressBar>
    );
  }, [progress, props.fileInfo, fileSize]);

  const fileName = useMemo(() => {
    if (props.disabledDownload)
      return (
        <span
          className={`dp-file-upload-area__file-name dp-file-upload-area__file-disable-download ${props.fileNameClassName}`}
          title={props.fileInfo.fileName}
          style={props.fontCssStyles}
        >
          {props.fileInfo.fileName}
        </span>
      );
    return (
      <Button
        variant="link"
        className={`dp-file-upload-area__file-name ${props.fileNameClassName}`}
        onClick={handleDownload}
        disabled={props.isDisabled}
        fontSize={'small'}
        fontWeight={'bold'}
        title={props.fileInfo.fileName}
        style={{ ...props.fontCssStyles, textTransform: 'initial' }}
      >
        {props.fileInfo.fileName}
      </Button>
    );
  }, [
    handleDownload,
    props.disabledDownload,
    props.fileInfo.fileName,
    props.isDisabled,
    props.fileNameClassName,
    props.fontCssStyles,
  ]);

  const holderClassNames = useMemo(() => {
    return classNames('dp-file-upload-area__file-info-root', {
      'dp-file-upload-area__uploaded-file': props.fileInfo.uploaded,
    });
  }, [props.fileInfo]);

  return (
    <div className={holderClassNames}>
      <div className="dp-file-upload-area__file-info">
        {fileName}
        <IconButton
          aria-label="clear"
          className="dp-file-upload-area__remove-file-button"
          onClick={handleRemove}
          disabled={props.isDisabled}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </div>
      {progressBar}
      {props.fileInfo.uploaded && <span className="dp-file-upload-area__file-size">{fileSize}</span>}
    </div>
  );
};
